import { FollowButtonOutline, FollowButtonSize, FollowButtonVariant } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "~/components/tracking/google-analytics/hooks";
import type { Event } from "~/components/tracking/google-analytics/types";
import type { articleTopicFollowWidgetArticle$key } from "~/queries/__generated__/articleTopicFollowWidgetArticle.graphql";

import type { StyledProps } from "./styles";
import {
  Container,
  Divider,
  StyledEntityFollowButton,
  StyledMyNewsPromotionWidget,
  StyledTopicLink,
} from "./styles";

type Props = {
  className?: string;
  reference: articleTopicFollowWidgetArticle$key;
  variant?: "banner" | "stack";
} & Partial<PropsFromStyledProps<StyledProps>>;
export const ArticleTopicFollowWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "banner",
}) => {
  const { topics } = useFragment(
    graphql`
      fragment articleTopicFollowWidgetArticle on Article {
        topics {
          ...topicLinkTopic
          ...entityFollowButtonBase
          disableFollow
        }
      }
    `,
    reference_,
  );

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Notification Center/Follow/Impression",
      category: "Notification Center",
      label: window.location.href,
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection(
    variant === "banner" ? impressionEvent : null,
    { shouldSendOnce: true },
  );

  if (!topics) return null;
  const mainTopic = first(topics);
  if (!mainTopic || mainTopic.disableFollow) return null;

  return (
    <Container
      $variant={variant}
      className={className}
      ref={captureTrackImpressionEventTargetElement}
    >
      <StyledTopicLink reference={mainTopic} />
      <Divider />
      <StyledEntityFollowButton
        outline={variant === "banner" ? FollowButtonOutline.Plain : FollowButtonOutline.Outlined}
        reference={mainTopic}
        size={variant === "banner" ? FollowButtonSize.Compact : FollowButtonSize.Small}
        variant={FollowButtonVariant.Light}
        isTooltipFlipDisabled
      />
      {variant === "stack" && <StyledMyNewsPromotionWidget />}
    </Container>
  );
};

ArticleTopicFollowWidget.displayName = "ArticleTopicFollowWidget";
